import { Box, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import html2pdf from "html2pdf.js";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import useAPI from "../../../../../api/hooks/useAPI";
import useColors from "../../../../../api/hooks/useColors";
import { dashboardKeys } from "../../../../../api/queryKeys";
import LoadingIcon from "../../../../../components/LoadingIcon";
import { Toast } from "../../../../../components/Toast";
import {
  FILE_EXTENSIONS,
  generateFileName,
} from "../../../../../hooks/useFileNaming";
import { LATEST, TimePoint } from "../../../../../utils/constants";
import { icons } from "../../../../stack/environment/icons/iconExport";
import WidgetWrapper from "../../WidgetWrapper";
import {
  baseTimePointOptions,
  calculateTrendData,
  createPdfConfig,
  createPdfContainer,
  createPdfHeader,
  createTrendSortMenuItems,
  enhanceAssetCardShadows,
  measureContentForPdf,
} from "../../utils/commonWidgetUtils";
import AssetCard from "./AssetCard";
import DonutPieChartPercentage from "./DonutPieChartPercentage";

const CriticalAssetPieWidget = forwardRef(({ mainTimePoint }, ref) => {
  const colors = useColors();
  const { getCriticalAssetsTrend, trackEvent } = useAPI();
  const [baseTimePoint, setBaseTimePoint] = useState(TimePoint.LATEST);
  const widgetContentRef = useRef(null);

  const { data: criticalAssetsTrend, isLoading: isLoadingCriticalAssetsTrend } =
    useQuery({
      queryKey: dashboardKeys.widgetType("criticalAssetsTrend"),
      queryFn: getCriticalAssetsTrend,
      select: (data) => data.data,
    });

  useEffect(() => {
    if (criticalAssetsTrend && criticalAssetsTrend[mainTimePoint]) {
      setBaseTimePoint(mainTimePoint);
    }
  }, [mainTimePoint]);

  const currentTimePoint = useMemo(() => {
    if (!criticalAssetsTrend || Object.keys(criticalAssetsTrend).length === 0)
      return {};
    return criticalAssetsTrend[LATEST];
  }, [criticalAssetsTrend]);

  // Calculate trend data for entities and coverage
  const trendData = useMemo(() => {
    if (!criticalAssetsTrend || !currentTimePoint) return {};
    const baseData = criticalAssetsTrend[baseTimePoint];
    return calculateTrendData(
      currentTimePoint,
      baseData,
      "attack_scenarios_count"
    );
  }, [currentTimePoint, criticalAssetsTrend, baseTimePoint]);

  const chartColors = [
    colors.primary_400,
    colors.primary_300,
    colors.primary_200,
    colors.primary_100,
    colors.primary_50,
    colors.primary_25,
  ];

  // Single source of truth - derive everything from currentTimePoint
  const { assetsPieChartDataToDisplay, topAssets } = useMemo(() => {
    if (!currentTimePoint)
      return { assetsPieChartDataToDisplay: [], topAssets: [] };

    const sortedAssets = Object.entries(currentTimePoint).sort(
      ([, aValue], [, bValue]) => bValue.coverage - aValue.coverage
    );

    const assetsPieChartDataToDisplay = sortedAssets.map(
      ([key, value], index) => ({
        id: index,
        asset_id: key,
        assetType: value.asset_type,
        value: value.coverage,
        scenariosCount: value.attack_scenarios_count,
        coverage: value.coverage,
        label: value.asset_name,
        color: undefined, // Color will be assigned after filtering
        trend: trendData[key] || null,
      })
    );

    const topAssets = sortedAssets.slice(0, 6).map(([key]) => key);

    return { assetsPieChartDataToDisplay, topAssets };
  }, [currentTimePoint, trendData, chartColors]);

  // Initialize selected assets as empty array
  const [selectedAssets, setSelectedAssets] = useState([]);

  // Update selected assets when top assets change (only if currently empty)
  useEffect(() => {
    if (selectedAssets.length === 0 && topAssets.length > 0) {
      setSelectedAssets(topAssets);
    }
  }, [topAssets, selectedAssets.length]);

  const filteredAssetsPieChartDataToDisplay = useMemo(() => {
    const filtered = assetsPieChartDataToDisplay.filter((item) =>
      selectedAssets.includes(item.asset_id)
    );

    // Assign unique colors to filtered assets after filtering
    return filtered.map((item, index) => ({
      ...item,
      color: chartColors[index], // Each selected asset gets a unique color
    }));
  }, [assetsPieChartDataToDisplay, selectedAssets, chartColors]);

  const sortMenuItems = createTrendSortMenuItems(
    baseTimePoint,
    setBaseTimePoint,
    criticalAssetsTrend,
    icons,
    trackEvent,
    "critical-assets-pie"
  );

  const MAX_ASSETS_SELECTED = 6;

  const selectedAssetsToDisplay = {
    title: "Select Assets",
    key: "selected-assets",
    disabled: false,
    submenuTitle: "Select Assets (Up to 6)",
    submenuMultiple: true,
    onclick: () => {},
    trailingIcon: icons.chevronRightIcon,
    submenu: assetsPieChartDataToDisplay.map((option) => ({
      title: option.label,
      key: option.id,
      selected: selectedAssets.includes(option.asset_id),
      onclick: () => {
        if (selectedAssets.includes(option.asset_id)) {
          setSelectedAssets((prev) =>
            prev.filter((asset) => asset !== option.asset_id)
          );
        } else {
          if (selectedAssets.length < MAX_ASSETS_SELECTED) {
            setSelectedAssets((prev) => [...prev, option.asset_id]);
          }
        }
      },
    })),
  };

  const handleExportToPdf = useCallback(
    async (isCombinedExport = false) => {
      if (!widgetContentRef.current || isLoadingCriticalAssetsTrend)
        return null;

      if (!isCombinedExport) {
        trackEvent("User clicked on Export to PDF button in Critical Assets");
      }

      try {
        if (isCombinedExport) {
          // For combined export, return just the widget content with header
          const widgetContainer = document.createElement("div");

          // Add widget header
          const widgetHeader = createPdfHeader("Critical Assets Report");
          widgetContainer.appendChild(widgetHeader);

          // Clone and style widget content
          const widgetClone = widgetContentRef.current.cloneNode(true);
          widgetClone.style.cssText = `
          background-color: white;
          padding: 24px;
          border: none;
          width: 100%;
          min-height: fit-content;
        `;

          // Enhance shadows for asset cards
          enhanceAssetCardShadows(widgetClone);
          widgetContainer.appendChild(widgetClone);

          return widgetContainer;
        } else {
          // Individual export - original functionality
          const entirePageElement = await createPdfContainer();

          const header = createPdfHeader("Critical Assets Report");
          entirePageElement.appendChild(header);

          // Clone and style widget content
          const widgetClone = widgetContentRef.current.cloneNode(true);
          widgetClone.style.cssText = `
          background-color: white;
          padding: 24px;
          border: none;
          width: 100%;
          min-height: fit-content;
        `;

          // Enhance shadows for asset cards
          enhanceAssetCardShadows(widgetClone);
          entirePageElement.appendChild(widgetClone);

          // Measure content dimensions
          const { contentWidth, contentHeight } = await measureContentForPdf(
            entirePageElement
          );

          // Generate PDF
          const pdfConfig = createPdfConfig(
            generateFileName(
              "Breeze_Critical_Assets",
              null,
              FILE_EXTENSIONS.PDF
            ),
            contentWidth,
            contentHeight
          );

          await html2pdf().set(pdfConfig).from(entirePageElement).save();
        }
      } catch (error) {
        Toast.error("Failed to export Critical Assets, Please try again later");
        return null;
      }
    },
    [widgetContentRef, isLoadingCriticalAssetsTrend]
  );

  // Expose the export function to parent components
  useImperativeHandle(
    ref,
    () => ({
      exportToPdf: handleExportToPdf,
      widgetId: "criticalAssets",
      hasExportCapability: true,
    }),
    [handleExportToPdf]
  );

  const assetPieChartDataToDisplay = filteredAssetsPieChartDataToDisplay.filter(
    (item) => item.value > 0 && item.scenariosCount > 0
  );
  return (
    <WidgetWrapper
      title={"Critical Assets"}
      sortMenuItems={{
        data: [...sortMenuItems, selectedAssetsToDisplay],
        tooltip: "View changes since selected time",
      }}
      info="Shows the critical assets with the lowest scenario coverage and their contribution to overall coverage."
      viewAll={{
        handleViewAll: () => {
          window.open("/inventory/assets", "_blank");
        },
        tooltip: "View all Critical Assets data",
        newTab: true,
      }}
      handleExportToPdf={() => handleExportToPdf(false)}
    >
      {isLoadingCriticalAssetsTrend ? (
        <Box>
          <LoadingIcon size={24} />
        </Box>
      ) : !currentTimePoint || Object.keys(currentTimePoint).length === 0 ? (
        <Box>
          <Typography variant="caption">No data available</Typography>
        </Box>
      ) : (
        <Box ref={widgetContentRef} sx={{ padding: "0px 16px 0px 16px" }}>
          <Typography variant="caption" marginLeft={2.5}>
            Current count{" "}
            {baseTimePoint !== LATEST
              ? `vs. ${baseTimePointOptions[baseTimePoint]}`
              : ""}
          </Typography>
          <Box
            display="flex"
            padding={2}
            flexDirection="row"
            gap={2}
            alignItems="center"
          >
            <Box
              display="flex"
              flexDirection="row"
              gap={2}
              flexWrap="wrap"
              width={"70%"}
            >
              {filteredAssetsPieChartDataToDisplay.map((item) => (
                <AssetCard
                  key={item.id}
                  name={item.label}
                  assetType={item.assetType}
                  value={item.scenariosCount}
                  coverage={item.coverage}
                  trend={item.trend}
                  baseTimePoint={baseTimePoint}
                />
              ))}
            </Box>

            <Box
              width={"30%"}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
              }}
            >
              <DonutPieChartPercentage
                data={assetPieChartDataToDisplay}
                type={"coverage"}
                label={"Coverage"}
                showPercent={true}
              />
            </Box>
          </Box>
        </Box>
      )}
    </WidgetWrapper>
  );
});

CriticalAssetPieWidget.displayName = "CriticalAssetPieWidget";

export default CriticalAssetPieWidget;
